/**
 * @file ManualPaymentForm.tsx
 *
 * The Manual payment form, mirroring WSB-v2: a breadcrumb header + a single
 * {@link ContentContainer} card with a Status radio group (Active/Inactive), the
 * payment-method name, optional Description and Instructions textareas (each with
 * help text), and an "Enable for local pick up only" checkbox; a Back / Save
 * footer. Owns the editable state via {@link useManualPaymentFormState},
 * validates the required name on save, and reports the fields to its parent.
 */

import { useId, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  Checkbox,
  ContentContainer,
  GeneralField,
  Icon,
  Radio,
  Textarea,
} from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { FormFooter } from '../../components/FormFooter';
import { FieldShell } from '../PaymentFormField';
import { useManualPaymentFormState } from './use-manual-payment-form-state';
import type { ManualPaymentFormFields } from './manual-payment-form-types';
import styles from '../payments.module.css';

interface ManualPaymentFormProps {
  /** The seed fields (stable reference; the hook re-seeds when it changes). */
  seed: ManualPaymentFormFields;
  saving: boolean;
  saveError?: string;
  onSubmit: (fields: ManualPaymentFormFields) => void;
  onCancel: () => void;
}

/**
 * Renders the Manual payment form.
 *
 * @param props - Seed fields, save state, and the submit/cancel handlers.
 *
 * @returns The manual payment form.
 */
export function ManualPaymentForm({
  seed,
  saving,
  saveError,
  onSubmit,
  onCancel,
}: ManualPaymentFormProps) {
  const intl = useIntl();
  const baseId = useId();
  const { fields, patch, errors, validate, firstErrorRef } = useManualPaymentFormState(seed);

  const id = (suffix: string): string => `${baseId}-${suffix}`;

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(fields);
  };

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/payments">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...adminMessages.paymentsManualTitle} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.paymentsManualDescription} />
        </div>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
          <div className="gv-form-option gv-w-full">
            <span className="gv-label">
              <FormattedMessage {...adminMessages.paymentsStatusLabel} />
            </span>
            <div className="gv-flex gv-flex-col gv-gap-md">
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="manual-payment-status-active"
              >
                <Radio
                  groupName="manual-payment-status"
                  checked={fields.enabled}
                  condensed
                  onChange={() => patch({ enabled: true })}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusActive} />
                </span>
              </label>
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="manual-payment-status-inactive"
              >
                <Radio
                  groupName="manual-payment-status"
                  checked={!fields.enabled}
                  condensed
                  onChange={() => patch({ enabled: false })}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusInactive} />
                </span>
              </label>
            </div>
          </div>

          <FieldShell id={id('name')} label={intl.formatMessage(adminMessages.paymentsManualName)}>
            <GeneralField
              id={id('name')}
              inputRef={firstErrorRef as RefObject<HTMLInputElement>}
              value={fields.title}
              error={errors.titleError}
              data-testid="manual-payment-name"
              placeholder={intl.formatMessage(adminMessages.paymentsManualNamePlaceholder)}
              aria-label={intl.formatMessage(adminMessages.paymentsManualName)}
              onChange={(event) => patch({ title: event.target.value })}
            />
          </FieldShell>

          <FieldShell
            id={id('description')}
            label={intl.formatMessage(adminMessages.paymentsManualDescriptionLabel)}
            error={errors.descriptionError}
            helper={intl.formatMessage(adminMessages.paymentsManualDescriptionHelp)}
          >
            <Textarea
              id={id('description')}
              value={fields.description}
              rows={3}
              data-testid="manual-payment-description"
              aria-label={intl.formatMessage(adminMessages.paymentsManualDescriptionLabel)}
              onChange={(event) => patch({ description: event.target.value })}
            />
          </FieldShell>

          <FieldShell
            id={id('instructions')}
            label={intl.formatMessage(adminMessages.paymentsManualInstructionsLabel)}
            optional
            helper={intl.formatMessage(adminMessages.paymentsManualInstructionsHelp)}
          >
            <Textarea
              id={id('instructions')}
              value={fields.instructions}
              rows={3}
              data-testid="manual-payment-instructions"
              aria-label={intl.formatMessage(adminMessages.paymentsManualInstructionsLabel)}
              onChange={(event) => patch({ instructions: event.target.value })}
            />
          </FieldShell>

          <label
            className="gv-flex gv-items-center gv-gap-sm"
            data-testid="manual-payment-local-pickup"
          >
            <Checkbox
              condensed
              checked={fields.enableForLocalPickupOnly}
              onChange={(checked) => patch({ enableForLocalPickupOnly: checked })}
            />
            <span className="gv-text-sm">
              <FormattedMessage {...adminMessages.paymentsManualLocalPickup} />
            </span>
          </label>
        </ContentContainer>

        {saveError ? (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="manual-payment-error"
          >
            {saveError}
          </p>
        ) : null}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="manual-payment-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="manual-payment-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.paymentsSaving} />
            ) : (
              <FormattedMessage {...adminMessages.paymentsSave} />
            )}
          </button>
        </FormFooter>
      </div>
    </div>
  );
}
